import { Category, Transaction } from "../types/transaction.type";
import { API } from "./base.service";
import { api } from "./config/axios";
import type { AsyncJobResponse } from "../types/job";

/**
 * TransactionAPI service for transaction-related endpoints
 *
 * IMPORTANT: As of the async job migration, the following endpoints now return
 * 202 Accepted with AsyncJobResponse (job_id, task_id, status_url) instead of
 * immediate results:
 *
 * - PUT /api/v1/transactions/upload/
 * - PUT /api/v1/transactions/upload_with_columns/
 * - GET /api/v1/transactions/train/
 * - GET /api/v1/transactions/train_user/
 * - POST /api/v1/transactions/predict_all/
 *
 * SYNCHRONOUS endpoints (unchanged):
 * - GET /api/v1/transactions/predict/ (single transaction prediction)
 * - POST /api/v1/transactions/bulk_create/
 * - PATCH /api/v1/transactions/bulk_update/
 * - DELETE /api/v1/transactions/bulk_delete/
 *
 * For async endpoints, callers must:
 * 1. Extract the job_id from the 202 response
 * 2. Poll /api/v1/jobs/{job_id}/ using JobAPI.getJob() until status is SUCCESS or FAILURE
 * 3. Handle progress updates via progress_percent and progress_message
 * 4. Extract final results from detailed_result (preferred) or result (metadata only)
 * 5. Handle cases where detailed_result is null due to Redis TTL expiration (~1 hour)
 *
 * See: docs/async-jobs-integration-guide.md for complete migration details
 */
class _TransactionAPI extends API {
  /**
   * Predict category for a single transaction (SYNCHRONOUS)
   *
   * This endpoint remains synchronous and returns immediate results.
   *
   * @param desc - Transaction description to predict category for
   * @returns Promise with predicted category data
   */
  predict = async (desc: string) => {
    const response = await api.request({
      url: `/${this.route}/predict/?desc=${desc}`,
      method: "GET",
    });

    return response;
  };

  /**
   * Predict categories for multiple transactions (ASYNC ENDPOINT)
   *
   * Returns 202 Accepted with job_id. Callers must poll JobAPI.getJob() for results.
   * The detailed_result will contain the predicted transactions array.
   *
   * @param transactions - Array of transactions to predict categories for
   * @param options - Request options including optional AbortSignal
   * @returns Promise with AsyncJobResponse { job_id, task_id, status_url }
   */
  predict_all = async (
    transactions: Partial<Transaction>[],
    options: { signal?: AbortSignal } = {}
  ): Promise<{ data: AsyncJobResponse }> => {
    const response = await api.request({
      url: `/${this.route}/predict_all/`,
      method: "POST",
      data: {
        transactions: transactions,
      },
      ...options,
    });

    return response;
  };

  /**
   * Upload a CSV file of transactions (ASYNC ENDPOINT)
   *
   * Returns 202 Accepted with job_id. Callers must poll JobAPI.getJob() for results.
   * The detailed_result will contain the parsed transactions array.
   *
   * @param file - CSV file to upload
   * @returns Promise with AsyncJobResponse { job_id, task_id, status_url }
   */
  upload = async (file: File): Promise<{ data: AsyncJobResponse }> => {
    const response = await api.request({
      url: `/${this.route}/upload/`,
      method: "PUT",
      data: { file: file },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response;
  };

  /**
   * Upload a CSV file with custom column mapping (ASYNC ENDPOINT)
   *
   * Returns 202 Accepted with job_id. Callers must poll JobAPI.getJob() for results.
   * The detailed_result will contain the parsed transactions array.
   *
   * @param formData - FormData containing file and column mappings
   * @returns Promise with AsyncJobResponse { job_id, task_id, status_url }
   */
  uploadWithColumns = async (
    formData: FormData
  ): Promise<{ data: AsyncJobResponse }> => {
    const response = await api.request({
      url: `/${this.route}/upload_with_columns/`,
      method: "PUT",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  };

  /**
   * Train the categorization model (ASYNC ENDPOINT)
   *
   * Returns 202 Accepted with job_id. Callers must poll JobAPI.getJob() for results.
   * The detailed_result will contain the training report.
   *
   * @returns Promise with AsyncJobResponse { job_id, task_id, status_url }
   */
  train = async (): Promise<{ data: AsyncJobResponse }> => {
    const response = await api.request({
      url: `/${this.route}/train/`,
      method: "GET",
    });

    return response;
  };

  /**
   * Train the user-specific categorization model (ASYNC ENDPOINT)
   *
   * Returns 202 Accepted with job_id. Callers must poll JobAPI.getJob() for results.
   * The detailed_result will contain the training report.
   *
   * @returns Promise with AsyncJobResponse { job_id, task_id, status_url }
   */
  trainUser = async (): Promise<{ data: AsyncJobResponse }> => {
    const response = await api.request({
      url: `/${this.route}/train_user/`,
      method: "GET",
    });

    return response;
  };

  /**
   * Bulk create transactions (SYNCHRONOUS)
   *
   * This endpoint remains synchronous and returns immediate results.
   *
   * @param transactions - Array of transactions to create
   * @param options - Request options including optional AbortSignal
   * @returns Promise with created transactions data
   */
  bulkCreate = async (
    transactions: any[],
    options: { signal?: AbortSignal } = {}
  ) => {
    const response = await api.request({
      url: `/${this.route}/bulk_create/`,
      method: "POST",
      data: {
        transactions: transactions,
      },
      ...options,
    });

    return response;
  };

  /**
   * Bulk update transactions (SYNCHRONOUS)
   *
   * This endpoint remains synchronous and returns immediate results.
   *
   * @param transactions - Array of transactions to update
   * @param category - Category to apply to all transactions
   * @returns Promise with updated transactions data
   */
  bulkPatch = async (transactions: any[], category: Category) => {
    const response = await api.request({
      url: `/${this.route}/bulk_update/`,
      method: "PATCH",
      data: {
        transactions: transactions,
        category: category,
      },
    });

    return response;
  };

  /**
   * Bulk delete transactions (SYNCHRONOUS)
   *
   * This endpoint remains synchronous and returns immediate results.
   *
   * @param transactions - Array of transactions to delete
   * @returns Promise with deletion result
   */
  bulkDelete = async (transactions: any[]) => {
    const response = await api.request({
      url: `/${this.route}/bulk_delete/`,
      method: "DELETE",
      data: {
        transactions: transactions,
      },
    });

    return response;
  };

  /**
   * Export transactions to Excel (ASYNC ENDPOINT)
   *
   * Returns 202 Accepted with job_id. Callers must poll JobAPI.getJob() for results.
   * The job result contains the download_url for retrieving the generated .xlsx file.
   * Files are stored in Redis with a 1-hour TTL, so downloads must happen promptly.
   *
   * Supports the same filter params as getAll (start_date, end_date, month, year,
   * category, type, description, created) with multi-select via repeated query params.
   *
   * Excel format: 4 sheets (Income, Expense, Investment, Unknown) with headers:
   * Occurred, Amount, Description, Category, Type, Created (UTC), Updated (UTC)
   *
   * @param params - Filter parameters matching those used in transactions list
   * @returns Promise with AsyncJobResponse { job_id, task_id, message, status_url, download_url }
   */
  exportTransactions = async (
    params?: any
  ): Promise<{ data: AsyncJobResponse }> => {
    const response = await api.request({
      url: `/${this.route}/export/`,
      method: "GET",
      params: params,
    });

    return response;
  };

  /**
   * Download exported transactions Excel file
   *
   * Downloads the generated .xlsx file for a completed export job.
   * File is cached in Redis with a 1-hour TTL after job completion.
   *
   * Status codes:
   * - 200: File is ready, returns streamed .xlsx file
   * - 202: Job is still PENDING/STARTED, not ready for download
   * - 404: Job doesn't exist, belongs to another user, or file expired (1-hour TTL)
   * - 500: Job failed or other server error
   *
   * @param jobId - The job_id from the export response
   * @returns Promise with Blob response for the Excel file
   */
  downloadExport = async (jobId: string) => {
    const response = await api.request({
      url: `/${this.route}/export/download/${jobId}/`,
      method: "GET",
      responseType: "blob",
    });

    return response;
  };
}

class _CategoryAPI extends API {
  getTotals = async (params?: any) => {
    const response = await api.request({
      url: `/${this.route}/totals/`,
      method: "GET",
      params: params,
    });

    return response;
  };
}

export const TransactionAPI = new _TransactionAPI("transactions");
export const TransactionTypeAPI = new API("transactions/types");
export const CategoryAPI = new _CategoryAPI("transactions/categories");
export const UserCategoryAPI = new API("transactions/categories/user");
export const MasterCategoryAPI = new API("transactions/categories/master");
